import { Checkbox, Modal } from '@bp/ui-components';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalBottomButtons } from '../components/ModalBottomButtons/ModalBottomButtons';

interface UseConfirmProps {
  defaultTitle?: string;
  defaultMessage?: string;
  defaultConfirmText?: string;
  defaultCancelText?: string;
  onConfirm?: (checked: boolean) => Promise<void>;
  checkboxText: string;
  checkboxDefaultChecked: boolean;
}

interface ConfirmProps {
  title?: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: (checked: boolean) => Promise<void>;
}

export const useConfirmWithCheckbox = ({
  defaultCancelText,
  defaultConfirmText,
  defaultMessage,
  defaultTitle,
  onConfirm,
  checkboxText,
  checkboxDefaultChecked,
}: UseConfirmProps) => {
  const [promise, setPromise] = useState<null | { resolve: (value: boolean, checked: boolean) => void }>(null);
  const [confirmText, setConfirmText] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [message, setMessage] = useState<ReactNode | null>(null);
  const [cancelText, setCancelText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [_onConfirm, _setOnConfirm] = useState<(checked: boolean) => Promise<void>>();
  const [_checked, _setChecked] = useState<boolean>(false);

  const { t } = useTranslation();
  const confirm = async (props?: ConfirmProps): Promise<{ confirmed: boolean; checked: boolean }> => {
    if (props?.title) {
      setTitle(props?.title);
    }
    if (props?.message) {
      setMessage(props?.message);
    }
    if (props?.confirmText) {
      setConfirmText(props?.confirmText);
    }
    if (props?.cancelText) {
      setCancelText(props?.cancelText);
    }
    if (props?.onConfirm) {
      _setOnConfirm(() => props.onConfirm);
    }
    return new Promise((resolve, reject) => {
      setPromise({ resolve: (value, checked) => resolve({ confirmed: value, checked: checked }) });
    });
  };

  useEffect(() => {
    if (checkboxDefaultChecked) {
      _setChecked(checkboxDefaultChecked);
    }
  }, [checkboxDefaultChecked]);

  useEffect(() => {
    setConfirmText(defaultConfirmText ?? null);
  }, [defaultConfirmText]);

  useEffect(() => {
    setTitle(defaultTitle ?? null);
  }, [defaultTitle]);

  useEffect(() => {
    setMessage(defaultMessage ?? null);
  }, [defaultMessage]);

  useEffect(() => {
    setCancelText(defaultCancelText ?? null);
  }, [defaultCancelText]);

  useEffect(() => {
    if (onConfirm) {
      _setOnConfirm(() => onConfirm);
    }
  }, [onConfirm]);

  const handleClose = () => {
    setPromise(null);
  };

  const handleConfirm = async () => {
    if (_onConfirm) {
      setLoading(true);
      await _onConfirm(_checked ?? false);
      setLoading(false);
    }
    promise?.resolve(true, _checked);
    handleClose();
  };

  const handleCancel = () => {
    promise?.resolve(false, _checked);
    handleClose();
  };

  const ConfirmationDialog = () => (
    <Modal
      isOpen={promise !== null}
      title={title ?? t('common.delete')}
      onRequestClose={handleCancel}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      height={'m'}
      width={'s'}
      style={{ overlay: { zIndex: `calc(var(--z-index-modal) + 1)` } }}
    >
      {message}

      <Checkbox
        className={'mt-4'}
        checked={_checked ?? false}
        label={checkboxText}
        name={'confirmCheckbox'}
        onChange={(event) => {
          _setChecked(event.target.checked);
        }}
      />
      <ModalBottomButtons
        submitButton={{ callback: handleConfirm, text: confirmText ?? t('common.confirm') }}
        closeButton={{ callback: handleCancel, text: cancelText ?? t('common.cancel') }}
        errors={false}
        isLoading={loading}
      />
    </Modal>
  );
  return { ConfirmationDialog, confirm };
};
